import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { KeyboardEvent, RefObject } from 'react'
import { InputMasked, Button } from '../../../../components'
import type { InputMaskedProps } from '../../../../components/InputMasked'
import type { NumberFormatOptionParams } from '../../../../components/number-format/NumberUtils'
import {
  formatCurrency,
  formatPercent,
  formatNumber,
} from '../../../../components/number-format/NumberUtils'
import type { InputAlign, InputSize } from '../../../../components/Input'
import SharedContext from '../../../../shared/Context'
import FieldBlockContext from '../../FieldBlock/FieldBlockContext'
import { clsx } from 'clsx'
import type { FieldBlockProps, FieldBlockWidth } from '../../FieldBlock'
import FieldBlock from '../../FieldBlock'
import { useFieldProps } from '../../hooks'
import type { FieldProps, Schema } from '../../types'
import { pickSpacingProps } from '../../../../components/flex/utils'
import type {
  ButtonProps,
  ButtonSize,
} from '../../../../components/Button'
import { clamp } from '../../../../shared/helpers/clamp'
import DataContext from '../../DataContext/Context'
import { FormError } from '../../utils'
import * as z from 'zod'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type FieldNumberProps = FieldProps<number, undefined | number> & {
  /** Ref to the underlying input element. */
  ref?: RefObject<HTMLInputElement>
  /** Additional CSS class applied to the inner input element. */
  inputClassName?: string
  /**
   * Currency code (ISO 4217) or `true` to use the default `NOK`. Uses two decimals by default.
   */
  currency?: InputMaskedProps['asCurrency']
  /** How to display the currency symbol: `code` (e.g., USD), `symbol` (e.g., $), `narrowSymbol`, or `name`. */
  currencyDisplay?: 'code' | 'symbol' | 'narrowSymbol' | 'name' | false
  /**
   * Format a number as a percentage.
   */
  percent?: InputMaskedProps['asPercent']
  /**
   * An array or a function returning an array of regexes to use as a mask for the input. If not given, the input will not be masked.
   */
  mask?: InputMaskedProps['mask']
  /**
   * Determines step granularity when increasing/decreasing the value via step control buttons or arrow keys. Defaults to `1`.
   */
  step?: number
  /**
   * When no `value` or `defaultValue` is given, start with a given value when increasing/decreasing the value via step control buttons or arrow keys. Defaults to `null`.
   */
  startWith?: number
  /**
   * Max number of decimals. Values with more decimals will be rounded. Defaults to `12`.
   */
  decimalLimit?: number
  /**
   * Whether or not to allow negative numbers. Defaults to `true`.
   */
  allowNegative?: boolean
  /**
   * Whether or not to allow leading zeroes during typing. Defaults to `false`.
   */
  disallowLeadingZeroes?: boolean
  /**
   * Text added before the value input.
   */
  prefix?: string | ((value: number) => string)
  /**
   * Text added after the value input.
   */
  suffix?: string | ((value: number) => string)
  /**
   * Validation for inclusive minimum number value (greater than or equal). Defaults to `Number.MIN_SAFE_INTEGER`.
   */
  minimum?: number
  /**
   * Validation for inclusive maximum number value (less than or equal). Defaults to `Number.MAX_SAFE_INTEGER`.
   */
  maximum?: number
  /**
   * Validation for exclusive minimum number value (greater than).
   */
  exclusiveMinimum?: number
  /**
   * Validation for exclusive maximum number value (less than).
   */
  exclusiveMaximum?: number
  /**
   * Validation that requires the number to be a multiple of a given value.
   */
  multipleOf?: number
  /** The size of the input. Available sizes: `small`, `medium` (default), `large`. */
  size?: InputSize
  /**
   * `false` for no width (use browser default), `small`, `medium` or `large` for predefined standard widths, `stretch` for fill available width.
   */
  width?: FieldBlockWidth
  /**
   * Lateral alignment of contents of input field, one of `left` (default), `center`, or `right`.
   */
  align?: InputAlign
  /**
   * Show buttons that increase/decrease the value by the step amount.
   */
  showStepControls?: boolean
  /** Text showing in place of the value if no value is given. */
  placeholder?: string
}

const defaultMinimum = Number.MIN_SAFE_INTEGER
const defaultMaximum = Number.MAX_SAFE_INTEGER

function NumberComponent(props: FieldNumberProps) {
  const dataContext = useContext(DataContext)
  const fieldBlockContext = useContext(FieldBlockContext)
  const sharedContext = useContext(SharedContext)
  const locale = sharedContext?.locale

  const validateContinuouslyRef = useRef(props?.validateContinuously)
  const [limitError, setLimitError] = useState<FormError | undefined>()

  const {
    currency,
    currencyDisplay,
    percent,
    mask,
    step = 1,
    decimalLimit = 12,
    allowNegative = true,
    disallowLeadingZeroes = false,
    prefix: prefixProp,
    suffix: suffixProp,
    showStepControls,
  } = props

  const schema = useMemo<Schema>(() => {
    return (
      // Use a factory so the schema is created using the current props
      // at validation time (min/max/exclusive/multipleOf). This keeps rules
      // in sync with dynamic prop changes and avoids stale closures.
      props.schema ??
      ((p: FieldNumberProps) => {
        // Helper function to format validation values with currency/percent suffix
        const formatValidationValue = (value: number) => {
          const formatOptions: Partial<NumberFormatOptionParams> = {
            locale,
          }

          if (p.decimalLimit !== undefined) {
            formatOptions.decimals = p.decimalLimit
          }

          if (p.percent) {
            return formatPercent(value, formatOptions)
          }

          if (p.currency) {
            return formatCurrency(value, {
              ...formatOptions,
              currency: p.currency,
            })
          }

          return formatNumber(value, formatOptions)
        }

        return z
          .number()
          .nullish()
          .superRefine((val, ctx) => {
            // Skip validation for null/undefined values (they are treated as empty)
            if (val === null || val === undefined) {
              return
            }
            // Default JavaScript safe integer limits.
            // The input mask prevents user-typed values from exceeding
            // these bounds, but programmatically set values (e.g. via
            // the value prop) can still bypass the mask.
            if (
              (p.minimum === undefined || p.minimum < defaultMinimum) &&
              val < defaultMinimum
            ) {
              ctx.addIssue({
                code: 'too_small',
                minimum: defaultMinimum,
                type: 'number',
                inclusive: true,
                message: 'NumberField.errorMinimum',
                messageValues: {
                  minimum: formatValidationValue(defaultMinimum),
                },
                origin: 'number',
                locale,
              })
            }

            if (
              (p.maximum === undefined || p.maximum > defaultMaximum) &&
              val > defaultMaximum
            ) {
              ctx.addIssue({
                code: 'too_big',
                maximum: defaultMaximum,
                type: 'number',
                inclusive: true,
                message: 'NumberField.errorMaximum',
                messageValues: {
                  maximum: formatValidationValue(defaultMaximum),
                },
                origin: 'number',
                locale,
              })
            }

            // minimum validation
            if (p.minimum !== undefined && val < p.minimum) {
              ctx.addIssue({
                code: 'too_small',
                minimum: p.minimum,
                type: 'number',
                inclusive: true,
                message: 'NumberField.errorMinimum',
                messageValues: {
                  minimum: formatValidationValue(p.minimum),
                },
                origin: 'number',
                locale,
              })
            }

            // maximum validation
            if (p.maximum !== undefined && val > p.maximum) {
              ctx.addIssue({
                code: 'too_big',
                maximum: p.maximum,
                type: 'number',
                inclusive: true,
                message: 'NumberField.errorMaximum',
                messageValues: {
                  maximum: formatValidationValue(p.maximum),
                },
                origin: 'number',
                locale,
              })
            }

            // exclusiveMinimum validation
            if (
              p.exclusiveMinimum !== undefined &&
              val <= p.exclusiveMinimum
            ) {
              ctx.addIssue({
                code: 'too_small',
                minimum: p.exclusiveMinimum,
                type: 'number',
                inclusive: false,
                message: 'NumberField.errorExclusiveMinimum',
                messageValues: {
                  exclusiveMinimum: formatValidationValue(
                    p.exclusiveMinimum
                  ),
                },
                origin: 'number',
                exclusiveMinimum: p.exclusiveMinimum,
                locale,
              })
            }

            // exclusiveMaximum validation
            if (
              p.exclusiveMaximum !== undefined &&
              val >= p.exclusiveMaximum
            ) {
              ctx.addIssue({
                code: 'too_big',
                maximum: p.exclusiveMaximum,
                type: 'number',
                inclusive: false,
                message: 'NumberField.errorExclusiveMaximum',
                messageValues: {
                  exclusiveMaximum: formatValidationValue(
                    p.exclusiveMaximum
                  ),
                },
                origin: 'number',
                exclusiveMaximum: p.exclusiveMaximum,
                locale,
              })
            }

            // multipleOf validation
            if (p.multipleOf !== undefined && val % p.multipleOf !== 0) {
              ctx.addIssue({
                code: 'custom',
                message: 'NumberField.errorMultipleOf',
                messageValues: {
                  multipleOf: formatValidationValue(p.multipleOf),
                },
                origin: 'number',
                multipleOf: p.multipleOf,
                locale,
              })
            }
          })
      })
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.schema,
    props.minimum,
    props.maximum,
    props.exclusiveMinimum,
    props.exclusiveMaximum,
    props.multipleOf,
    props.currency,
    props.percent,
    props.decimalLimit,
    locale,
  ])

  const toInput = useCallback((external: number | undefined | unknown) => {
    if (external === undefined || external === null) {
      return null
    }
    // Handle invalid types (e.g., strings) and non-finite numbers
    // (NaN, Infinity, -Infinity) by converting to empty string for display.
    // Such values still fail validation, but should not render as partial
    // output (e.g. "-" for -Infinity).
    if (typeof external !== 'number' || !Number.isFinite(external)) {
      return ''
    }
    return external
  }, [])
  const fromInput = useCallback(
    ({
      value,
      numberValue,
      cleanedValue,
    }: {
      value: string
      numberValue: number
      cleanedValue: string
    }) => {
      if (value === '' || cleanedValue === '') {
        return props.emptyValue
      }

      // When the user types just a minus sign, numberValue is -0.
      // Return emptyValue to keep the display showing the minus
      // without converting the value to 0.
      if (allowNegative && Object.is(numberValue, -0)) {
        return props.emptyValue
      }

      return numberValue
    },
    [props.emptyValue, allowNegative]
  )

  const ref = useRef<HTMLInputElement>(undefined)
  const preparedProps: FieldNumberProps = {
    valueType: 'number',
    validateContinuously: validateContinuouslyRef.current,
    ...props,
    error: limitError || props.error,
    schema,
    toInput,
    fromInput,
    width:
      props.width ??
      (fieldBlockContext?.composition ? 'stretch' : 'medium'),
    ref: props.ref ?? ref,
  }

  const {
    id,
    name,
    className,
    ref: inputRef,
    inputClassName,
    autoComplete,
    placeholder,
    value,
    startWith = null,
    minimum = defaultMinimum,
    maximum = defaultMaximum,
    disabled,
    htmlAttributes,
    hasError,
    size,
    width,
    align,
    handleFocus,
    handleBlur,
    handleChange,
    setDisplayValue,
  } = useFieldProps(preparedProps)

  useEffect(() => {
    // Use getElementById to read the current DOM input value
    const input = id ? document.getElementById(id) : null
    setDisplayValue((input as HTMLInputElement)?.value)
  }, [id, setDisplayValue, value])

  const { handleSubmit } = dataContext ?? {}
  const onKeyDownHandler = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const { event } = e as unknown as {
        event: KeyboardEvent<HTMLInputElement>
      }
      if (dataContext?.props?.isolate && event.key === 'Enter') {
        handleSubmit() // So we commit the data to the outer context
        event.preventDefault?.() // And prevent the default form submit
      }

      if (!showStepControls) {
        return
      }

      let numberValue = null

      switch (event.key) {
        case 'ArrowUp':
          numberValue = clamp(
            (value ?? startWith) + step,
            minimum,
            maximum
          )
          break
        case 'ArrowDown':
          numberValue = clamp(
            (value ?? startWith) - step,
            minimum,
            maximum
          )
          break
      }

      if (numberValue !== null) {
        event.preventDefault()
        handleChange({ numberValue })
      }
    },
    [
      dataContext?.props?.isolate,
      handleChange,
      handleSubmit,
      maximum,
      minimum,
      showStepControls,
      startWith,
      step,
      value,
    ]
  )

  const onChangeHandler = useCallback(
    (args: { numberValue?: number; stringValue?: string }) => {
      if (limitError) {
        setLimitError(undefined)
      }

      handleChange(args)
    },
    [handleChange, limitError]
  )

  const fieldBlockProps: FieldBlockProps = {
    forId: id,
    className: clsx(
      'dnb-forms-field-number',
      'dnb-input__border--tokens', // Used by "dnb-input__border"
      className
    ),
    contentClassName: clsx(
      'dnb-forms-field-number__contents',
      showStepControls && 'dnb-forms-field-number__contents--has-controls',
      hasError && 'dnb-input__status--error', // Also used by "dnb-input__border"
      disabled && 'dnb-input--disabled' // Also used by "dnb-input__border"
    ),
    width:
      (width === 'stretch' || fieldBlockContext?.composition) &&
      !showStepControls
        ? width
        : undefined,
    contentWidth: width !== false ? width : undefined,
    ...pickSpacingProps(props),
  }

  const increaseClickHandler = useCallback(() => {
    handleChange({
      numberValue: clamp((value ?? startWith) + step, minimum, maximum),
    })
  }, [handleChange, maximum, minimum, startWith, step, value])

  const increaseProps: ButtonProps = showStepControls && {
    'aria-hidden': true,
    className: 'dnb-button--control-after',
    variant: 'secondary',
    icon: 'add',
    size: (size || 'small') as ButtonSize,
    tabIndex: -1,
    disabled: disabled || value >= maximum,
    onClick: increaseClickHandler,
    title: sharedContext?.translation.Slider.addTitle?.replace(
      '%s',
      String(value + step)
    ),
    status: hasError ? 'error' : undefined,
  }

  const decreaseClickHandler = useCallback(() => {
    handleChange({
      numberValue: clamp((value ?? startWith) - step, minimum, maximum),
    })
  }, [handleChange, maximum, minimum, startWith, step, value])

  const decreaseProps: ButtonProps = showStepControls && {
    ...increaseProps,
    className: 'dnb-button--control-before',
    icon: 'subtract',
    size: (size || 'small') as ButtonSize,
    disabled: disabled || value <= minimum,
    onClick: decreaseClickHandler,
    title: sharedContext?.translation.Slider.subtractTitle?.replace(
      '%s',
      String(value - step)
    ),
  }

  const prefix =
    typeof prefixProp === 'function' ? prefixProp(value) : prefixProp
  const suffix =
    typeof suffixProp === 'function' ? suffixProp(value) : suffixProp

  const maskProps: Partial<InputMaskedProps> = useMemo(() => {
    const onRejectSafeInteger = (num: number) => {
      const isMax = num > 0
      setLimitError(
        new FormError(
          `NumberField.${isMax ? 'errorMaximum' : 'errorMinimum'}`,
          {
            messageValues: {
              [isMax ? 'maximum' : 'minimum']: formatNumber(
                isMax ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER,
                { locale }
              ),
            },
          }
        )
      )
    }

    const maskOptions = {
      prefix,
      suffix,
      decimalLimit,
      allowNegative,
      disallowLeadingZeroes,
      onRejectSafeInteger,
    }

    if (currency) {
      return {
        asCurrency: currency,
        maskOptions,
        currencyMask: {
          currencyDisplay,
          decimalLimit,
        },
      }
    }

    if (percent) {
      return {
        asPercent: percent,
        maskOptions,
      }
    }

    // Custom mask based on props
    return {
      mask,
      asNumber: mask ? undefined : true,
      numberMask: mask ? undefined : maskOptions,
    }
  }, [
    currency,
    currencyDisplay,
    decimalLimit,
    mask,
    percent,
    prefix,
    suffix,
    allowNegative,
    disallowLeadingZeroes,
    locale,
  ])

  const ariaParams = showStepControls && {
    role: 'spinbutton' as const,
    'aria-valuemin': minimum,
    'aria-valuemax': maximum,
    'aria-valuenow': value ?? 0, // without it, VO will read an invalid value
    'aria-valuetext': String(value ?? ''), // without it, VO will read %
  }

  const inputProps = {
    id,
    name,
    ref: inputRef,
    autoComplete,
    className: clsx(
      'dnb-forms-field-number__input',
      `dnb-input--${size}`,
      inputClassName
    ),
    step: showStepControls ? step : undefined,
    placeholder,
    value,
    align: showStepControls ? 'center' : align,
    onKeyDown: onKeyDownHandler,
    onPaste: handleBlur,
    onFocus: handleFocus,
    onBlur: () => {
      setLimitError(undefined)
      handleBlur()
    },
    onChange: onChangeHandler,
    disabled,
    status: hasError ? 'error' : undefined,
    stretch: Boolean(width),
    ...maskProps,
    ...htmlAttributes,
    ...(ariaParams || {}),
  }

  if (showStepControls) {
    return (
      <FieldBlock {...fieldBlockProps} asFieldset={false}>
        <span className="dnb-input__border dnb-input__border--root">
          <Button {...decreaseProps} />
          <InputMasked {...inputProps} />
          <Button {...increaseProps} />
        </span>
      </FieldBlock>
    )
  }

  return (
    <FieldBlock {...fieldBlockProps} asFieldset={false}>
      <InputMasked {...inputProps} />
    </FieldBlock>
  )
}

withComponentMarkers(NumberComponent, {
  _supportsSpacingProps: true,
})

export default NumberComponent
